import { useLazyQuery } from '@apollo/client';

import { copyToClipBoard } from 'common/copyToClipboard';
import useToast from 'components/ToastProvider/useToast';
import logger from 'helpers/logger';

import { useCardDetailsContext } from '../../context';
import { CardSecurityDetailsDocument } from './cardSecurityDetails.gql';
import locales from './locales';

const useCardSecurityDetails = ({
  creditCardId,
  shouldCopy,
}: {
  creditCardId: string;
  shouldCopy?: boolean;
}) => {
  const [getCardSecurityDetailsQuery, { loading }] = useLazyQuery(
    CardSecurityDetailsDocument,
  );
  const { showToast } = useToast();

  const { setCardDetails } = useCardDetailsContext();

  const copyPANToClipBoard = (PAN: string) => {
    copyToClipBoard(PAN);
    showToast({
      message: locales.successCopy,
      type: 'success',
    });
  };

  const getCardSecurityDetails = async (token: string) => {
    try {
      const { data } = await getCardSecurityDetailsQuery({
        context: {
          headers: {
            'short-lived-token': token,
          },
        },
        variables: { creditCardId },
      });

      if (!data) {
        throw new Error('Failed to get card details');
      }

      setCardDetails({
        CVV: data.cardSecurityDetails.CVV,
        PAN: data.cardSecurityDetails.PAN,
      });

      if (shouldCopy) {
        copyPANToClipBoard(data.cardSecurityDetails.PAN);
      }
      return true;
    } catch (error) {
      showToast({ message: locales.error, type: 'error' });
      logger.error(error, {
        context: { creditCardId },
      });
      return false;
    }
  };

  return {
    copyPANToClipBoard,
    fetchingSecurityDetails: loading,
    getCardSecurityDetails,
  };
};

export default useCardSecurityDetails;
